import type {
  RegulatedCoveredEntity,
  RegulatedPreviousVertical,
  RegulatedProfileDisplay,
  RegulatedProfileInput,
  RegulatedVertical,
} from '@heyroot/contracts/tenants/entitlements/regulated-profile';
import { authenticatedFetch } from './authenticated-fetch';
import { isLegalProfile } from '@heyroot/contracts/tenants/entitlements/regulated-profile';

export interface TenantListRow {
  id: string;
  slug: string;
  displayName: string;
  status: string;
  createdAt: string;
  locationsCount: number;
  profitCentersCount: number;
  terminalsCount: number;
  usersCount: number;
}
export interface TenantListParams {
  search?: string;
  limit?: number;
  offset?: number;
  sort?: 'created_at' | 'display_name' | 'status';
  dir?: 'asc' | 'desc';
}
export interface TenantDetail {
  id: string;
  slug: string;
  displayName: string;
  businessName: string | null;
  primaryEmail: string;
  status: string;
  businessType: string;
  defaultCurrency: string;
  isFinalized: boolean;
  createdAt: string;
  activatedAt: string | null;
  trialEndsAt: string | null;
}
export interface Entitlement {
  moduleKey: string;
  accessMode: string;
  source: string;
  startsAt: string | null;
  endsAt: string | null;
  // #12993 — AI usage-limit overrides live in the module's `limits` object
  // (insights_monthly_questions/summaries, assistant_monthly_questions).
  // Absent on an older/rolling-deployment payload, so callers treat a
  // missing field the same as {}.
  limits?: Record<string, unknown>;
}
export interface TenantNote {
  id: string;
  tenantId: string;
  authorUid: string;
  body: string;
  createdAt: string;
  updatedAt: string;
}
export interface TenantDetailResponse {
  tenant: TenantDetail;
  entitlements: Entitlement[];
  notes: TenantNote[];
}

// â”€â”€â”€ Assistant â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface EscalationRow {
  id: string;
  tenantId: string;
  conversationId: string;
  userId: string;
  question: string;
  assistantResponse: string | null;
  confidenceTier: string | null;
  confidenceScore: number | null;
  escalationReason: string | null;
  status: string;
  createdAt: string;
  closedAt: string | null;
  resolutionNotes: string | null;
  resolvedBy: string | null;
}

export interface ReviewLogRow {
  id: string;
  escalationId: string;
  staffUserId: string;
  verdict: string;
  notes: string | null;
  createdAt: string;
}

export interface EscalationDetail extends EscalationRow {
  reviews: ReviewLogRow[];
}

export interface ListEscalationsParams {
  tenantId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

export async function fetchEscalations(
  p: ListEscalationsParams,
): Promise<{ rows: EscalationRow[]; total: number }> {
  const q = new URLSearchParams();
  if (p.tenantId) q.set('tenantId', p.tenantId);
  if (p.status) q.set('status', p.status);
  if (p.startDate) q.set('startDate', p.startDate);
  if (p.endDate) q.set('endDate', p.endDate);
  if (p.limit != null) q.set('limit', String(p.limit));
  if (p.offset != null) q.set('offset', String(p.offset));
  return json(await authenticatedFetch(`/api/assistant/escalations?${q.toString()}`));
}

export async function fetchEscalationDetail(id: string): Promise<EscalationDetail> {
  return json(await authenticatedFetch(`/api/assistant/escalations/${id}`));
}

export async function submitReview(
  escalationId: string,
  verdict: 'approved' | 'edited' | 'rejected',
  notes: string | null,
): Promise<ReviewLogRow> {
  return json(
    await authenticatedFetch(`/api/assistant/escalations/${escalationId}/reviews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ verdict, notes }),
    }),
  );
}

export interface ChatMessageRow {
  id: string;
  tenantId: string;
  escalationId: string;
  authorType: string;
  authorId: string;
  body: string;
  createdAt: string;
}

export async function fetchChatMessages(escalationId: string): Promise<ChatMessageRow[]> {
  const res = await json<{ messages: ChatMessageRow[] }>(
    await authenticatedFetch(`/api/assistant/escalations/${escalationId}/chat`),
  );
  return res.messages;
}

export async function sendChatMessage(escalationId: string, body: string): Promise<ChatMessageRow> {
  return json(
    await authenticatedFetch(`/api/assistant/escalations/${escalationId}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    }),
  );
}

export async function resolveEscalation(
  escalationId: string,
  resolutionNotes: string | null,
): Promise<EscalationRow> {
  return json(
    await authenticatedFetch(`/api/assistant/escalations/${escalationId}/resolve`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resolutionNotes }),
    }),
  );
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

// â”€â”€â”€ Knowledge base â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface KnowledgeRow {
  id: string;
  tenantId: string;
  category: string;
  entryKey: string;
  status: string;
  sourceEntryKey: string | null;
  updatedBy: string | null;
  questionVariants: string[];
  answer: string;
  confidenceTier: string;
  confidenceThreshold: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertKnowledgeBody {
  tenantId: string;
  category: string;
  entryKey: string;
  questionVariants: string[];
  answer: string;
  confidenceTier: 'high' | 'medium' | 'low';
  confidenceThreshold: number;
  sourceEntryKey?: string | null;
}

export async function fetchKnowledgeEntries(tenantId: string): Promise<KnowledgeRow[]> {
  const res = await json<{ entries: KnowledgeRow[] }>(
    await authenticatedFetch(`/api/assistant/knowledge?tenantId=${encodeURIComponent(tenantId)}`),
  );
  return res.entries;
}

export async function upsertKnowledgeEntry(body: UpsertKnowledgeBody): Promise<KnowledgeRow> {
  return json(
    await authenticatedFetch('/api/assistant/knowledge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
}

export async function disableKnowledgeEntry(id: string): Promise<KnowledgeRow> {
  return json(
    await authenticatedFetch(`/api/assistant/knowledge/${id}/disable`, { method: 'PATCH' }),
  );
}

export interface MetricsDailyRow {
  date: string;
  tenantId: string;
  conversationsCount: number;
  escalationsCount: number;
  avgResolutionMinutes: number | null;
  csatAvg: number | null;
  confidenceTierDistribution: { low: number; medium: number; high: number };
}

export interface AnalyticsParams {
  from: string;
  to: string;
  tenantId?: string | null;
}

// ─── Cost-plus messaging billing ─────────────────────────────────────────

export type MessagingChannel = 'email' | 'sms' | 'push';
export type BillingUnit = 'contact' | 'send';
export type PlanMode = 'contact' | 'send';
export type RevenueModel = 'flat' | 'markup' | 'none';

export interface SupplierRow {
  supplierKey: string;
  displayName: string;
  channels: MessagingChannel[];
  billingUnit: BillingUnit;
  enabled: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface UpsertSupplierBody {
  supplierKey: string;
  displayName: string;
  channels: MessagingChannel[];
  billingUnit: BillingUnit;
  enabled: boolean;
  notes: string | null;
}
export interface SupplyRateRow {
  id: string;
  supplierKey: string;
  channel: MessagingChannel;
  billingUnit: BillingUnit;
  buyRateCents: number | null;
  currency: string;
  effectiveFrom: string;
  createdAt: string;
}
export interface AddRateBody {
  channel: MessagingChannel;
  billingUnit: BillingUnit;
  buyRateCents: number | null;
}
export interface TenantPlanRow {
  tenantId: string;
  planMode: PlanMode;
  assignedSupplierKey: string | null;
  sellRateCents: number | null;
  markupPct: number | null;
  currency: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface UpsertPlanBody {
  planMode: PlanMode;
  assignedSupplierKey: string | null;
  sellRateCents: number | null;
  markupPct: number | null;
  enabled: boolean;
}
export interface MarginLine {
  tenantId: string;
  periodStart: string;
  planMode: PlanMode | null;
  assignedSupplierKey: string | null;
  resolvedChannel: MessagingChannel | null;
  buyRateCents: number | null;
  sellRateCents: number | null;
  markupPct: number | null;
  currency: string;
  hasPlan: boolean;
  usage: number;
  costCents: number | null;
  revenueCents: number | null;
  marginCents: number | null;
  revenueModel: RevenueModel;
}
export interface MarginRollup {
  tenantCount: number;
  tenantsCounted: number;
  costCents: number;
  revenueCents: number;
  marginCents: number;
}
export interface PlatformMargin {
  periodStart: string;
  lines: MarginLine[];
  rollup: MarginRollup;
}

// ─── Onboarding ─────────────────────────────────────────────────────────────

export interface OnboardingQueueItem {
  /** Optional during rolling deployment against an older root API. */
  guideProgress?: { completed: number; total: number; percentage: number };
  tenantId: string;
  tenantName: string;
  onboardingStepsCompleted: number;
  onboardingCurrentStep: number | null;
  stallDay: number | null;
  claimStatus: string;
  // Added PR-6:
  daysSinceActivity: number | null;
  claimExpiresAt: string | null;
}

export interface OnboardingQueueResponse {
  items: OnboardingQueueItem[];
  nextCursor: string | null;
}

export interface OnboardingQueueStats {
  needsCs: number;
  unclaimedStuck: number;
  staleClaims: number;
  goliveLastSevenDays: number;
  csInterventionInflowSevenDays: number;
}

export async function fetchOnboardingQueueStats(): Promise<OnboardingQueueStats> {
  const envelope = await json<{ data: OnboardingQueueStats; requestId: string }>(
    await authenticatedFetch('/api/onboarding/queue/stats'),
  );
  return envelope.data;
}

export interface FetchOnboardingQueueParams {
  status?: string;
  cursor?: string;
  limit?: number;
}

export async function fetchOnboardingQueue(
  p: FetchOnboardingQueueParams = {},
): Promise<OnboardingQueueResponse> {
  const q = new URLSearchParams();
  if (p.status && p.status !== 'all') q.set('status', p.status);
  if (p.cursor) q.set('cursor', p.cursor);
  if (p.limit != null) q.set('limit', String(p.limit));
  const qs = q.toString();
  const envelope = await json<{ data: OnboardingQueueResponse; requestId: string }>(
    await authenticatedFetch(`/api/onboarding/queue${qs ? `?${qs}` : ''}`),
  );
  return envelope.data;
}

export interface OnboardingEventRow {
  id: string;
  action: string;
  adminEmail: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface OnboardingEventsResponse {
  items: OnboardingEventRow[];
  nextCursor: string | null;
}

export interface OnboardingProgressEventRow {
  id: string;
  stepKey: string;
  stepLabel: string | null;
  kind: string;
  source: string;
  actor: string | null;
  eventId: string | null;
  occurredAt: string;
}

export interface OnboardingProgressEventsResponse {
  items: OnboardingProgressEventRow[];
  nextCursor: string | null;
}

export async function fetchOnboardingProgressEvents(
  tenantId: string,
  cursor: string | null,
  limit: number,
): Promise<OnboardingProgressEventsResponse> {
  const q = new URLSearchParams();
  if (cursor) q.set('cursor', cursor);
  q.set('limit', String(limit));
  const envelope = await json<{ data: OnboardingProgressEventsResponse; requestId: string }>(
    await authenticatedFetch(
      `/api/onboarding/${encodeURIComponent(tenantId)}/progress-events?${q}`,
    ),
  );
  return envelope.data;
}

export async function fetchOnboardingEvents(
  tenantId: string,
  cursor?: string,
  limit?: number,
): Promise<OnboardingEventsResponse> {
  const q = new URLSearchParams();
  if (cursor) q.set('cursor', cursor);
  if (limit != null) q.set('limit', String(limit));
  const qs = q.toString();
  const envelope = await json<{ data: OnboardingEventsResponse; requestId: string }>(
    await authenticatedFetch(
      `/api/onboarding/${encodeURIComponent(tenantId)}/events${qs ? `?${qs}` : ''}`,
    ),
  );
  return envelope.data;
}

export async function claimOnboardingTenant(tenantId: string): Promise<void> {
  await json(
    await authenticatedFetch(`/api/onboarding/${encodeURIComponent(tenantId)}/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }),
  );
}

// ─── Feedback triage (support-feedback PR2) ──────────────────────────────────

export interface FetchFeedbackParams {
  status?: string;
  module?: string;
  requestType?: string;
  priority?: string;
  tenantId?: string;
  /** Substring over title + description. */
  q?: string;
  /** 'me' = assigned to the signed-in operator; 'unassigned' = nobody has picked it up. */
  assignee?: 'me' | 'unassigned';
  sort?: 'newest' | 'oldest';
  cursor?: string;
  limit?: number;
}

export interface AdminFeedbackDto {
  id: string;
  tenantId: string;
  locationId: string | null;
  submittedBy: string;
  submittedByName: string | null;
  submittedByEmail: string | null;
  requestType: string;
  module: string;
  submodule: string | null;
  title: string;
  description: string;
  businessImpact: string | null;
  priority: string;
  additionalNotes: string | null;
  currentWorkaround: string | null;
  status: string;
  adminNotes: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolvedByName: string | null;
  voteCount: number;
  context: Record<string, unknown>;
  tags: string[];
  rowVersion: number;
  createdAt: string;
  updatedAt: string;
  /** Note shown to the submitter (completed/declined only). Optional during rolling deployment. */
  publicNote?: string | null;
  assigneeUid?: string | null;
  assigneeName?: string | null;
  /** Display name / slug of the submitting tenant (null when the lookup found nothing). */
  tenantName?: string | null;
  tenantSlug?: string | null;
}

export interface FeedbackListResponse {
  rows: AdminFeedbackDto[];
  nextCursor: string | null;
}

export async function fetchFeedbackSubmissions(
  p: FetchFeedbackParams = {},
): Promise<FeedbackListResponse> {
  const qs = feedbackQueryString(p);
  return json(await authenticatedFetch(`/api/feedback/submissions${qs ? `?${qs}` : ''}`));
}

function feedbackQueryString(p: FetchFeedbackParams): string {
  const q = new URLSearchParams();
  for (const key of [
    'status',
    'module',
    'requestType',
    'priority',
    'tenantId',
    'q',
    'assignee',
    'sort',
  ] as const) {
    const v = p[key];
    if (v) q.set(key, v);
  }
  if (p.cursor) q.set('cursor', p.cursor);
  if (p.limit != null) q.set('limit', String(p.limit));
  return q.toString();
}

export interface FeedbackSummaryResponse {
  counts: Record<string, number>;
  total: number;
}

/** Per-status counts for the inbox chips; honors every filter except status. */
export async function fetchFeedbackSummary(
  p: Omit<FetchFeedbackParams, 'status' | 'cursor' | 'limit' | 'sort'> = {},
): Promise<FeedbackSummaryResponse> {
  const qs = feedbackQueryString(p);
  return json(await authenticatedFetch(`/api/feedback/summary${qs ? `?${qs}` : ''}`));
}

export async function fetchFeedbackSubmission(id: string): Promise<AdminFeedbackDto> {
  return json(await authenticatedFetch(`/api/feedback/submissions/${encodeURIComponent(id)}`));
}

export interface UpdateFeedbackBody {
  status?: string;
  adminNotes?: string | null;
  /** Sent to the submitter; only accepted on the move INTO completed/declined. */
  publicNote?: string | null;
  assignee?: 'me' | 'none';
  expectedVersion: number;
}

export async function updateFeedbackSubmission(
  id: string,
  body: UpdateFeedbackBody,
): Promise<AdminFeedbackDto> {
  return json(
    await authenticatedFetch(`/api/feedback/submissions/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
}

export interface AdminAttachmentDto {
  id: string;
  feedbackId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  avScanStatus: 'pending' | 'clean' | 'infected' | 'scan_failed';
  source: 'manual_upload' | 'screen_capture';
  createdAt: string;
}

export async function fetchFeedbackAttachments(id: string): Promise<AdminAttachmentDto[]> {
  const res = await json<{ attachments: AdminAttachmentDto[] }>(
    await authenticatedFetch(`/api/feedback/submissions/${encodeURIComponent(id)}/attachments`),
  );
  return res.attachments;
}

export interface EraseFeedbackResult {
  erased: true;
  attachmentsDeleted: number | null;
  erasedAt: string | null;
}

/**
 * Erase one submission and its screenshots on a privacy request (#14561). `requestRef` is the privacy-request
 * log reference. A 502 `ERASE_STORAGE_INCOMPLETE` means the record was kept (some screenshots may already be gone); see
 * isEraseStorageIncomplete in use-feedback.ts.
 */
export async function eraseFeedbackSubmission(
  id: string,
  requestRef: string,
): Promise<EraseFeedbackResult> {
  return json(
    await authenticatedFetch(`/api/feedback/submissions/${encodeURIComponent(id)}/erase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requestRef }),
    }),
  );
}

export async function fetchAttachmentSignedUrl(id: string, attachmentId: string): Promise<string> {
  const res = await json<{ data: { url: string } }>(
    await authenticatedFetch(
      `/api/feedback/submissions/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}/signed-url`,
    ),
  );
  return res.data.url;
}

// ─────────────────────────────────────────────────────────────────────────────
// User management (Task 10)

export interface PersonRow {
  id: string;
  email: string;
  createdAt: string;
  tenantIds: string[];
  tenantNames: string[];
  roles: string[];
  hasActiveMembership: boolean;
  tenantCount: number;
  // Fix round 2 finding 1: identity.users.status, the ACCOUNT-level status
  // (distinct from tenant_users.status, which is per-membership). Null when
  // no identity.users mirror exists yet for this legacy platform.users row.
  accountStatus: string | null;
  accountStatusChangedAt: string | null;
}
export interface MembershipRow {
  tenantId: string;
  tenantName: string;
  role: string;
  status: string;
  invitedAt: string | null;
  acceptedAt: string | null;
  allowedLocationIds: string[];
}
export interface StaffRow {
  uid: string;
  email: string;
  displayName: string | null;
  status: string;
  role: string | null;
  createdAt: string;
  invitedAt: string | null;
  lastLoginAt: string | null;
  suspendedAt: string | null;
  suspendReason: string | null;
}
export interface AdminRoleRow {
  roleId: string;
  name: string;
  description: string | null;
  permissions: string[];
  isBuiltin: boolean;
  assigneeCount: number;
  roleDefVersion: string;
  createdAt: string;
  updatedAt: string;
}

export interface PeopleParams {
  q?: string;
  status?: string;
  tenantId?: string;
  limit: number;
  offset: number;
}

function qs(params: Record<string, string | number | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  return sp.toString();
}

export async function fetchPeople(p: PeopleParams): Promise<{ rows: PersonRow[]; total: number }> {
  // Round-1 fix (Finding 1): the router wraps the payload in { data }
  // (users-router.ts:121) -- unwrap it here so the return type matches what
  // callers actually get, not the raw envelope.
  const res = await json<{ data: { rows: PersonRow[]; total: number } }>(
    await authenticatedFetch(`/api/users?${qs({ ...p })}`),
  );
  return res.data;
}

export async function fetchPerson(
  userId: string,
): Promise<{ person: PersonRow; memberships: MembershipRow[] }> {
  // Round-1 fix (Finding 1): users-router.ts:131 wraps in { data } too.
  const res = await json<{ data: { person: PersonRow; memberships: MembershipRow[] } }>(
    await authenticatedFetch(`/api/users/${encodeURIComponent(userId)}`),
  );
  return res.data;
}

export async function sendPasswordReset(userId: string): Promise<void> {
  await mutate(`/api/users/${encodeURIComponent(userId)}/password-reset`, 'POST');
}

export async function setUserAccountStatus(
  userId: string,
  body: { status: 'active' | 'suspended'; reason?: string },
): Promise<void> {
  await mutate(`/api/users/${encodeURIComponent(userId)}/status`, 'POST', body);
}

export async function fetchStaff(p: {
  q?: string;
  status?: string;
  role?: string;
  limit: number;
  offset: number;
}): Promise<{ rows: StaffRow[]; total: number }> {
  // Round-1 fix (Finding 1): staff-router.ts:51 wraps in { data }.
  const res = await json<{ data: { rows: StaffRow[]; total: number } }>(
    await authenticatedFetch(`/api/staff?${qs({ ...p })}`),
  );
  return res.data;
}

export async function fetchStaffMember(uid: string): Promise<StaffRow> {
  // Round-1 fix (Finding 1): staff-router.ts:59 wraps in { data }.
  const res = await json<{ data: StaffRow }>(
    await authenticatedFetch(`/api/staff/${encodeURIComponent(uid)}`),
  );
  return res.data;
}

export async function inviteStaff(body: {
  email: string;
  roleId: string;
  displayName?: string;
}): Promise<StaffRow> {
  // Round-1 fix (Finding 2): staff-router.ts:81 responds { data: <full StaffRow> },
  // not { uid }. The prior `as { uid: string }` cast papered over the same
  // envelope mismatch as Finding 1 rather than adapting to mutate()'s
  // non-generic Promise<unknown> return -- unwrap consistently instead.
  const res = (await mutate('/api/staff', 'POST', body)) as { data: StaffRow };
  return res.data;
}

export async function setStaffRole(uid: string, roleId: string): Promise<void> {
  await mutate(`/api/staff/${encodeURIComponent(uid)}`, 'PATCH', { roleId });
}

export async function setStaffStatus(
  uid: string,
  body: { status: 'active' | 'suspended'; reason?: string },
): Promise<void> {
  await mutate(`/api/staff/${encodeURIComponent(uid)}/status`, 'POST', body);
}

export async function deleteStaff(uid: string, reason: string): Promise<void> {
  await mutate(`/api/staff/${encodeURIComponent(uid)}`, 'DELETE', { reason });
}

export async function fetchAdminRoles(): Promise<AdminRoleRow[]> {
  // Round-1 fix (Finding 1): roles-router.ts:37 wraps in { data }.
  const res = await json<{ data: AdminRoleRow[] }>(await authenticatedFetch('/api/admin-roles'));
  return res.data;
}

export async function fetchAdminRole(roleId: string): Promise<AdminRoleRow> {
  // Round-1 fix (Finding 1): roles-router.ts:42 wraps in { data }.
  const res = await json<{ data: AdminRoleRow }>(
    await authenticatedFetch(`/api/admin-roles/${encodeURIComponent(roleId)}`),
  );
  return res.data;
}

export async function createAdminRole(body: {
  roleId: string;
  name: string;
  description?: string;
  permissions: string[];
}): Promise<void> {
  await mutate('/api/admin-roles', 'POST', body);
}

export async function updateAdminRole(
  roleId: string,
  body: { name: string; description?: string; permissions: string[] },
): Promise<void> {
  await mutate(`/api/admin-roles/${encodeURIComponent(roleId)}`, 'PATCH', body);
}

export async function deleteAdminRole(roleId: string, reason: string): Promise<void> {
  await mutate(`/api/admin-roles/${encodeURIComponent(roleId)}`, 'DELETE', { reason });
}

// ─────────────────────────────────────────────────────────────────────────────

export const queryKeys = {
  tenants: (p: TenantListParams) => ['tenants', p] as const,
  tenant: (id: string) => ['tenant', id] as const,
  // Not nested under `tenant`: a note, entitlement or AI-limit save invalidates that prefix, and
  // none of them changes the regulated profile.
  regulatedProfile: (id: string) => ['tenant-regulated-profile', id] as const,
  moduleKeys: ['module-keys'] as const,
  businessTypes: (f: BusinessTypeFilters = {}) => ['business-types', f] as const,
  businessType: (slug: string) => ['business-type', slug] as const,
  businessTypeCategories: ['business-type-categories'] as const,
  escalations: (p: ListEscalationsParams) => ['assistant-escalations', p] as const,
  escalation: (id: string) => ['assistant-escalation', id] as const,
  chatMessages: (id: string) => ['assistant-chat', id] as const,
  knowledgeEntries: (tenantId: string) => ['assistant-knowledge', tenantId] as const,
  analytics: (p: AnalyticsParams) => ['assistant-analytics', p] as const,
  costPlusSuppliers: ['cost-plus-suppliers'] as const,
  costPlusRates: (supplierKey: string) => ['cost-plus-rates', supplierKey] as const,
  costPlusPlans: ['cost-plus-plans'] as const,
  costPlusMargin: (period: string) => ['cost-plus-margin', period] as const,
  onboardingQueue: (status: string) => ['onboarding-queue', status] as const,
  onboardingEvents: (tenantId: string) => ['onboarding-events', tenantId] as const,
  feedbackSubmissions: (p: FetchFeedbackParams) => ['feedback-submissions', p] as const,
  // Shares the 'feedback-submissions' prefix so a status change (which invalidates that prefix)
  // refreshes the chip counts too.
  feedbackSummary: (p: Record<string, unknown>) => ['feedback-submissions', 'summary', p] as const,
  feedbackSubmission: (id: string) => ['feedback-submission', id] as const,
  feedbackAttachments: (id: string) => ['feedback-attachments', id] as const,
  people: (p: PeopleParams) => ['people', p] as const,
  person: (id: string) => ['person', id] as const,
  staff: (p: Record<string, unknown>) => ['staff', p] as const,
  staffMember: (uid: string) => ['staff-member', uid] as const,
  adminRoles: ['admin-roles'] as const,
  adminRole: (id: string) => ['admin-role', id] as const,
  tenantOrganization: (tenantId: string, includeArchived: boolean) =>
    ['tenant-organization', tenantId, includeArchived] as const,
  tenantOrgLocations: (tenantId: string, includeArchived: boolean) =>
    ['tenant-org-locations', tenantId, includeArchived] as const,
  tenantOrgProfitCenters: (tenantId: string, locationId: string, includeArchived: boolean) =>
    ['tenant-org-profit-centers', tenantId, locationId, includeArchived] as const,
  tenantOrgTerminals: (tenantId: string, profitCenterId: string, includeArchived: boolean) =>
    ['tenant-org-terminals', tenantId, profitCenterId, includeArchived] as const,
  tenantUsers: (tenantId: string, cursor?: string) => ['tenant-users', tenantId, cursor] as const,
  tenantRoles: (tenantId: string, includeArchived: boolean) =>
    ['tenant-roles', tenantId, includeArchived] as const,
  tenantRole: (tenantId: string, roleId: string) => ['tenant-role', tenantId, roleId] as const,
  tenantAudit: (tenantId: string, cursor?: string) => ['tenant-audit', tenantId, cursor] as const,
  tenantPermissions: ['tenant-permissions'] as const,
  moduleMatrix: (p: ModuleMatrixParams) => ['module-matrix', p] as const,
  pendingInvites: (tenantId: string) => ['pending-invites', tenantId] as const,
};

/**
 * Canonical entitlement module keys (served from @heyroot/contracts, no client
 * copy). `criticalModuleKeys` are the risk='critical' modules whose changes the
 * mutation engine requires a reason for (master plan section 4.4) — the console
 * prompts for one up-front. `deprecatedModuleKeys` are retired keys (blocked
 * from new grants server-side; filtered from the "add a module" picker here as
 * defense in depth). `moduleLabels` / `moduleDependencies` are sparse maps —
 * only keys with a friendly label / a non-empty dependsOn are present, so
 * callers fall back to the raw key / no-dependency display for every other key.
 */
export interface ModuleKeysMeta {
  moduleKeys: string[];
  criticalModuleKeys: string[];
  deprecatedModuleKeys: string[];
  moduleLabels: Record<string, string>;
  moduleDependencies: Record<string, string[]>;
}
export async function fetchModuleKeys(): Promise<ModuleKeysMeta> {
  const res = await json<Partial<ModuleKeysMeta>>(
    await authenticatedFetch('/api/meta/module-keys'),
  );
  return {
    moduleKeys: res.moduleKeys ?? [],
    criticalModuleKeys: res.criticalModuleKeys ?? [],
    deprecatedModuleKeys: res.deprecatedModuleKeys ?? [],
    moduleLabels: res.moduleLabels ?? {},
    moduleDependencies: res.moduleDependencies ?? {},
  };
}

// Surfaced on thrown errors so callers (e.g. useUpdateFeedbackSubmission) can
// branch on upstream error codes (409 VERSION_CONFLICT, 422 ILLEGAL_TRANSITION)
// without re-parsing the response body themselves.
export interface ApiError extends Error {
  status: number;
  code?: string;
  body?: unknown;
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof Error && 'status' in err;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = '';
    let code: string | undefined;
    let body: unknown;
    try {
      body = await res.json();
      const b = body as { message?: unknown; error?: unknown; code?: string };
      // Live-found bug (2026-08-20): the server's `message`/`error` field is
      // not guaranteed to be a string -- coerce so a malformed error body
      // never renders as the literal "[object Object]" in the UI.
      const raw = b.message ?? b.error;
      detail = raw == null ? '' : typeof raw === 'string' ? raw : JSON.stringify(raw);
      code = b.code;
    } catch {
      // non-JSON error body; fall through to status-only message
    }
    const err = new Error(
      detail ? `${res.status}: ${detail}` : `Request failed: ${res.status}`,
    ) as ApiError;
    err.status = res.status;
    if (code) err.code = code;
    if (body !== undefined) err.body = body;
    throw err;
  }
  return res.json() as Promise<T>;
}

export async function fetchTenants(
  p: TenantListParams,
): Promise<{ rows: TenantListRow[]; total: number }> {
  const q = new URLSearchParams();
  if (p.search) q.set('search', p.search);
  if (p.limit != null) q.set('limit', String(p.limit));
  if (p.offset != null) q.set('offset', String(p.offset));
  if (p.sort) q.set('sort', p.sort);
  if (p.dir) q.set('dir', p.dir);
  return json(await authenticatedFetch(`/api/tenants?${q.toString()}`));
}

export async function fetchTenantDetail(id: string): Promise<TenantDetailResponse> {
  return json(await authenticatedFetch(`/api/tenants/${id}`));
}

// #15687: a tenant's regulated profile and its change history. Mirrors the server's
// RegulatedProfileView (server/tenants/regulated-profile-repo.ts): only parsed fields reach the browser.
export interface RegulatedProfileActor {
  uid: string;
  email: string | null;
}
export type RegulatedProfileCurrent =
  | { state: 'not_recorded' }
  | { state: 'unreadable' }
  | {
      state: 'recorded';
      vertical: RegulatedVertical;
      coveredEntity: RegulatedCoveredEntity;
      evidenceRef: string;
      attestedBy: RegulatedProfileActor;
      attestedAt: string;
    };
export interface RegulatedProfileHistoryRow {
  at: string;
  changeType: string;
  actor: RegulatedProfileActor | null;
  reason: string | null;
  before: RegulatedProfileDisplay | null;
  after: RegulatedProfileDisplay | null;
}
export interface RegulatedProfileView {
  current: RegulatedProfileCurrent;
  history: { total: number; rows: RegulatedProfileHistoryRow[] };
}

/**
 * The legal earned-fees guards apply to this tenant: a recorded law firm, or a record that cannot be read (the guards
 * treat it as a law firm). False for any other state, and for a view that has not loaded (#16377).
 */
export function regulatedGuardsApply(view: RegulatedProfileView | undefined): boolean {
  const current = view?.current;
  if (current === undefined) return false;
  // Rebuild what the server's guards read from this view and ask the server's own rule, so the two cannot drift.
  if (current.state === 'not_recorded') return isLegalProfile({ recorded: false });
  return isLegalProfile({
    recorded: true,
    vertical: current.state === 'unreadable' ? 'unknown' : current.vertical,
    coveredEntity: current.state === 'unreadable' ? 'unknown' : current.coveredEntity,
  });
}
export interface RegulatedProfileSaveBody {
  reason: string;
  // The contracts input type the BFF validates against (RegulatedProfileInputSchema), not a copy.
  regulatedProfile: RegulatedProfileInput;
  /**
   * The vertical this page loaded (`unknown` for an unreadable record, `not_recorded` for none). The engine refuses
   * the save when the stored vertical differs, so a stale page never overwrites a concurrent correction (#16378).
   */
  expectedFromVertical?: RegulatedPreviousVertical;
  /** The tenant slug the operator typed into the remove-guards confirm; the engine requires it to take a law firm off `legal`. */
  confirmSlug?: string;
}

// Built on mutate() so the caller keeps the BFF's error `code` (UNAUTHORIZED, NOT_FOUND) and the
// module's message; json() would flatten both into a string.
export async function fetchRegulatedProfile(tenantId: string): Promise<RegulatedProfileView> {
  return mutate(
    `/api/tenants/${encodeURIComponent(tenantId)}/regulated-profile`,
    'GET',
  ) as Promise<RegulatedProfileView>;
}

/**
 * Record or correct the profile. A 503 `identity_cache_invalidation_failed` means the write
 * COMMITTED (the guards read the row directly) but some cached views may lag; it resolves with
 * `cacheRefreshDelayed: true`. Any other failure rejects. Not every rejection means nothing
 * changed: a 502 `UPSTREAM_ERROR` (root-api answered but its response was lost), a gateway 502 or
 * 504 relayed from root-api's front end (#16626), or a dropped connection (not an AdminApiError at
 * all) may follow a committed write, so callers re-read.
 */
export async function saveRegulatedProfile(
  tenantId: string,
  body: RegulatedProfileSaveBody,
): Promise<{ cacheRefreshDelayed: boolean }> {
  try {
    await mutate(`/api/tenants/${encodeURIComponent(tenantId)}/regulated-profile`, 'PUT', body);
    return { cacheRefreshDelayed: false };
  } catch (e) {
    if (
      e instanceof AdminApiError &&
      e.status === 503 &&
      e.code === 'identity_cache_invalidation_failed'
    ) {
      return { cacheRefreshDelayed: true };
    }
    throw e;
  }
}

// Fix round 2 (C1): the server's statusSchema (tenants-router.ts) requires a
// non-empty `reason` for EVERY status target, not just the destructive ones --
// it always did, since this slice's own commit 1b3921ba9 added the field.
// This function shipped without it and every Apply 400'd. `reason` is
// required here to match, not optional -- there is no legal call site that
// can omit it.
export async function updateTenantStatus(
  id: string,
  status: string,
  reason: string,
): Promise<void> {
  await json(
    await authenticatedFetch(`/api/tenants/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, reason }),
    }),
  );
}

export async function createNote(tenantId: string, body: string): Promise<{ id: string }> {
  return json(
    await authenticatedFetch(`/api/tenants/${tenantId}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    }),
  );
}

export async function updateNote(noteId: string, body: string): Promise<void> {
  await json(
    await authenticatedFetch(`/api/notes/${noteId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body }),
    }),
  );
}

export async function deleteNote(noteId: string): Promise<void> {
  await json(await authenticatedFetch(`/api/notes/${noteId}`, { method: 'DELETE' }));
}

// ─── Business Types registry (Slice 2) ───────────────────────────────────────
// Reads are slug-addressed (matches the FE route and admin search deep-links);
// writes are id-addressed (detail() returns the id). Keep that split — it's
// deliberate (business-types-repo.ts header).

export interface BusinessTypeListRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  categoryId: string | null;
  categoryName: string | null;
  isSystem: boolean;
  isActive: boolean;
  showAtSignup: boolean;
  sortOrder: number;
  publishedVersionNumber: number | null;
  hasDraft: boolean;
  moduleCount: number;
  roleCount: number;
}
export interface BusinessTypeVersion {
  id: string;
  versionNumber: number;
  status: 'draft' | 'published' | 'archived';
  changeSummary: string | null;
  taxProvider: string | null;
  receiptTemplate: string | null;
  courseDefinitions: Record<string, unknown>;
  publishedAt: string | null;
  publishedBy: string | null;
  modules: { moduleKey: string; isRecommended: boolean }[];
  roles: { name: string; permissions: string[] }[];
}
export type BusinessTypeDetail = Omit<
  BusinessTypeListRow,
  'publishedVersionNumber' | 'hasDraft' | 'moduleCount' | 'roleCount'
> & {
  versions: BusinessTypeVersion[];
};
export interface BusinessTypeCategory {
  id: string;
  name: string;
}
export interface BusinessTypeFilters {
  categoryId?: string;
  isActive?: boolean;
  isSystem?: boolean;
  showAtSignup?: boolean;
  search?: string;
}
export interface ValidationIssue {
  code: string;
  message: string;
}

// Typed error surface: the UI renders SLUG_TAKEN against a field and
// VERSION_NOT_PUBLISHABLE per issue, so the client must carry `code` and
// `issues`, not just a message (task-10-brief §Typed errors).
export class AdminApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: ValidationIssue[] = [],
    // Task-13 fix: roles-router.ts's ROLE_IN_USE response is
    // `{ error: { code, assigneeCount } }` -- no `message` field at all. Without
    // capturing arbitrary extra error-envelope keys, the UI had no way to render
    // "N operators still have this role" and would fall back to the generic
    // `Request failed (409)` text, silently dropping the one number the operator
    // needs to act on the guard.
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

async function mutate(path: string, method: string, body?: unknown): Promise<unknown> {
  const res = await authenticatedFetch(path, {
    method,
    ...(body !== undefined
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      : {}),
  });
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : {};
  if (!res.ok) {
    // Most admin-lane errors use the nested `{ error: { code, message } }`
    // envelope, but the shared admin-service auth middleware (root-api,
    // proxied through unchanged) answers its 401/503 with a FLAT
    // `{ code, message }` body (secrets-overhaul design.md §11). Fall back to
    // the flat shape so the real code survives instead of becoming UNKNOWN.
    const p = parsed as { error?: Record<string, unknown>; code?: unknown };
    const e =
      p.error && typeof p.error === 'object'
        ? p.error
        : typeof p.code === 'string'
          ? (p as Record<string, unknown>)
          : undefined;
    const { code, message, issues, ...details } = e ?? {};
    throw new AdminApiError(
      res.status,
      typeof code === 'string' ? code : 'UNKNOWN',
      typeof message === 'string' ? message : `Request failed (${res.status})`,
      Array.isArray(issues) ? (issues as ValidationIssue[]) : [],
      details,
    );
  }
  return parsed;
}

export async function fetchBusinessTypes(
  f: BusinessTypeFilters = {},
): Promise<{ rows: BusinessTypeListRow[] }> {
  const q = new URLSearchParams();
  if (f.categoryId !== undefined) q.set('categoryId', f.categoryId);
  if (f.isActive !== undefined) q.set('isActive', String(f.isActive));
  if (f.isSystem !== undefined) q.set('isSystem', String(f.isSystem));
  if (f.showAtSignup !== undefined) q.set('showAtSignup', String(f.showAtSignup));
  if (f.search) q.set('search', f.search);
  const qs = q.toString();
  return mutate(`/api/business-types${qs ? `?${qs}` : ''}`, 'GET') as Promise<{
    rows: BusinessTypeListRow[];
  }>;
}

export async function fetchBusinessType(slug: string): Promise<BusinessTypeDetail> {
  return mutate(
    `/api/business-types/${encodeURIComponent(slug)}`,
    'GET',
  ) as Promise<BusinessTypeDetail>;
}

export async function fetchBusinessTypeCategories(): Promise<{ rows: BusinessTypeCategory[] }> {
  return mutate('/api/business-type-categories', 'GET') as Promise<{
    rows: BusinessTypeCategory[];
  }>;
}

export async function createBusinessType(b: {
  name: string;
  slug: string;
  categoryId?: string | null;
  description?: string | null;
  showAtSignup?: boolean;
  sortOrder?: number;
}): Promise<{ data: { id: string } }> {
  return mutate('/api/business-types', 'POST', b) as Promise<{ data: { id: string } }>;
}

// No caller yet: type metadata (name/description/category/showAtSignup/sortOrder)
// is intentionally immutable after creation until a type-metadata editor is
// built -- out of this slice's scope (rail + Modules/Roles editors + read-only
// Accounting tab only). Keep the client function ready for that follow-up.
export async function updateBusinessType(
  id: string,
  patch: Partial<{
    name: string;
    categoryId: string | null;
    description: string | null;
    showAtSignup: boolean;
    sortOrder: number;
  }>,
): Promise<void> {
  await mutate(`/api/business-types/${encodeURIComponent(id)}`, 'PATCH', patch);
}

export async function setBusinessTypeActive(id: string, isActive: boolean): Promise<void> {
  await mutate(`/api/business-types/${encodeURIComponent(id)}/activate`, 'PATCH', { isActive });
}

export async function createDraftVersion(
  id: string,
): Promise<{ data: { id: string; versionNumber: number } }> {
  return mutate(`/api/business-types/${encodeURIComponent(id)}/versions`, 'POST') as Promise<{
    data: { id: string; versionNumber: number };
  }>;
}

export async function replaceVersionModules(
  vid: string,
  modules: { moduleKey: string; isRecommended: boolean }[],
): Promise<void> {
  // Wire contract is an object, not a bare array -- ModulesBody =
  // z.object({ modules: ... }) (modules/tenants/server/admin-business-types-routes.ts).
  await mutate(`/api/business-types/versions/${encodeURIComponent(vid)}/modules`, 'PUT', {
    modules,
  });
}

export async function upsertVersionRole(
  vid: string,
  role: { name: string; permissions: string[] },
): Promise<void> {
  await mutate(`/api/business-types/versions/${encodeURIComponent(vid)}/roles`, 'POST', role);
}

export async function putVersionRole(
  vid: string,
  name: string,
  permissions: string[],
): Promise<void> {
  await mutate(
    `/api/business-types/versions/${encodeURIComponent(vid)}/roles/${encodeURIComponent(name)}`,
    'PUT',
    { permissions },
  );
}

export async function deleteVersionRole(vid: string, name: string): Promise<void> {
  await mutate(
    `/api/business-types/versions/${encodeURIComponent(vid)}/roles/${encodeURIComponent(name)}`,
    'DELETE',
  );
}

export async function validateVersion(
  vid: string,
): Promise<{ data: { issues: ValidationIssue[] } }> {
  return mutate(
    `/api/business-types/versions/${encodeURIComponent(vid)}/validate`,
    'GET',
  ) as Promise<{ data: { issues: ValidationIssue[] } }>;
}

export async function publishVersion(vid: string, changeSummary: string | null): Promise<void> {
  await mutate(`/api/business-types/versions/${encodeURIComponent(vid)}/publish`, 'POST', {
    changeSummary,
  });
}

export async function setEntitlement(
  tenantId: string,
  moduleKey: string,
  // undefined on a limits-only save: the server re-reads the current mode.
  accessMode: string | undefined,
  reason?: string,
  // #12993 — present REPLACES the module's whole `limits` object server-side
  // (entitlement-mutation.ts); omit to leave existing limits untouched.
  // Existing call sites (which never pass this) keep their old behavior.
  limits?: Record<string, number>,
  // #13656 — the limits this save was based on. The server answers 409
  // LIMITS_CHANGED when the stored limits no longer equal them.
  expectedLimits?: Record<string, number>,
): Promise<void> {
  await json(
    await authenticatedFetch(`/api/tenants/${tenantId}/entitlements/${moduleKey}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accessMode,
        ...(reason ? { reason } : {}),
        ...(limits !== undefined ? { limits } : {}),
        ...(limits !== undefined && expectedLimits !== undefined ? { expectedLimits } : {}),
      }),
    }),
  );
}

// ── Organization (Task 13 step 1) ────────────────────────────────────────────
// GET /organization stays the fast aggregate-counts path (locations only,
// with profitCentersCount/terminalsCount) -- the per-level row lists below
// are separate drill-down reads, each proxying the module's own GET so this
// client never re-derives a status vocabulary or `version` field the module
// already owns (see organization-router.ts's own header comment).
export interface OrganizationLocationRow {
  id: string;
  name: string;
  status: 'active' | 'inactive' | 'closed' | 'archived';
  profitCentersCount: number;
  terminalsCount: number;
}
export interface OrganizationOverview {
  locations: OrganizationLocationRow[];
}
export async function fetchTenantOrganization(
  tenantId: string,
  includeArchived: boolean,
): Promise<OrganizationOverview> {
  const q = includeArchived ? '?includeArchived=true' : '';
  return json(await authenticatedFetch(`/api/tenants/${tenantId}/organization${q}`));
}

// Full location/profit-center/terminal wire rows (module-owned shape, incl.
// `version` for the edit drawer's optimistic-concurrency PATCH). Only the
// fields the tabs actually render/edit are typed; the module sends more.
export interface WireLocationRow {
  id: string;
  name: string;
  status: 'active' | 'inactive' | 'closed' | 'archived';
  version: number;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  stateProvince: string | null;
  postalCode: string | null;
  country: string | null;
  timezone: string;
  currencyCode: string;
}
export interface WireProfitCenterRow {
  id: string;
  locationId: string;
  name: string;
  code: string | null;
  // Profit centers use a 3-value vocabulary (no 'closed') -- a DIFFERENT
  // enum from locations' 4-value one. Do not share the type.
  status: 'active' | 'inactive' | 'archived';
  version: number;
}
export interface WireTerminalRow {
  id: string;
  profitCenterId: string;
  locationId: string;
  name: string;
  terminalNumber: string | null;
  // Terminals also use the 3-value vocabulary, same as profit centers.
  status: 'active' | 'inactive' | 'archived';
  version: number;
}
export async function fetchOrgLocations(
  tenantId: string,
  includeArchived: boolean,
): Promise<{ rows: WireLocationRow[] }> {
  const q = includeArchived ? '?includeArchived=true' : '';
  return json(await authenticatedFetch(`/api/tenants/${tenantId}/organization/locations${q}`));
}
export async function fetchOrgProfitCenters(
  tenantId: string,
  locationId: string,
  includeArchived: boolean,
): Promise<{ rows: WireProfitCenterRow[] }> {
  const q = new URLSearchParams({ locationId });
  if (includeArchived) q.set('includeArchived', 'true');
  return json(
    await authenticatedFetch(`/api/tenants/${tenantId}/organization/profit-centers?${q}`),
  );
}
export async function fetchOrgTerminals(
  tenantId: string,
  profitCenterId: string,
  includeArchived: boolean,
): Promise<{ rows: WireTerminalRow[] }> {
  const q = new URLSearchParams({ profitCenterId });
  if (includeArchived) q.set('includeArchived', 'true');
  return json(await authenticatedFetch(`/api/tenants/${tenantId}/organization/terminals?${q}`));
}
export async function createOrgLocation(
  tenantId: string,
  body: Record<string, unknown>,
): Promise<{ row: WireLocationRow }> {
  return mutate(`/api/tenants/${tenantId}/organization/locations`, 'POST', body) as Promise<{
    row: WireLocationRow;
  }>;
}
export async function updateOrgLocation(
  tenantId: string,
  locationId: string,
  expectedVersion: number,
  patch: Record<string, unknown>,
): Promise<{ row: WireLocationRow }> {
  return mutate(`/api/tenants/${tenantId}/organization/locations/${locationId}`, 'PATCH', {
    expectedVersion,
    patch,
  }) as Promise<{ row: WireLocationRow }>;
}
export async function createOrgProfitCenter(
  tenantId: string,
  body: Record<string, unknown>,
): Promise<{ row: WireProfitCenterRow }> {
  return mutate(`/api/tenants/${tenantId}/organization/profit-centers`, 'POST', body) as Promise<{
    row: WireProfitCenterRow;
  }>;
}
export async function updateOrgProfitCenter(
  tenantId: string,
  profitCenterId: string,
  expectedVersion: number,
  patch: Record<string, unknown>,
): Promise<{ row: WireProfitCenterRow }> {
  return mutate(`/api/tenants/${tenantId}/organization/profit-centers/${profitCenterId}`, 'PATCH', {
    expectedVersion,
    patch,
  }) as Promise<{ row: WireProfitCenterRow }>;
}
export async function createOrgTerminal(
  tenantId: string,
  body: Record<string, unknown>,
): Promise<{ row: WireTerminalRow }> {
  return mutate(`/api/tenants/${tenantId}/organization/terminals`, 'POST', body) as Promise<{
    row: WireTerminalRow;
  }>;
}
export async function updateOrgTerminal(
  tenantId: string,
  terminalId: string,
  expectedVersion: number,
  patch: Record<string, unknown>,
): Promise<{ row: WireTerminalRow }> {
  return mutate(`/api/tenants/${tenantId}/organization/terminals/${terminalId}`, 'PATCH', {
    expectedVersion,
    patch,
  }) as Promise<{ row: WireTerminalRow }>;
}

// ── Tenant users (Task 13 step 2) ────────────────────────────────────────────
// Fan-out rows -- a user with two roles in one tenant is TWO rows sharing
// userId, distinguished by roleId. A role-less member still gets exactly one
// row (roleId: null). React key MUST be the composite (tenantId, userId,
// roleId) -- a plain userId key silently drops the second row (slice 3's
// trap 9).
export interface TenantUserRow {
  userId: string;
  roleId: string | null;
  roleName: string | null;
  isCustomRole: boolean | null;
  grantedAt: string | null;
  locations: string[];
  profile: {
    username: string | null;
    externalPayrollId: string | null;
    tabColor: string | null;
    status: 'active' | 'suspended';
  } | null;
}
export async function fetchTenantUsers(
  tenantId: string,
  opts: { cursor?: string; limit?: number } = {},
): Promise<{ rows: TenantUserRow[]; nextCursor: string | null }> {
  const q = new URLSearchParams();
  if (opts.cursor) q.set('cursor', opts.cursor);
  if (opts.limit != null) q.set('limit', String(opts.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return json(await authenticatedFetch(`/api/tenants/${tenantId}/tenant-users${suffix}`));
}
export async function inviteTenantUser(
  tenantId: string,
  body: { email: string; roleId: string; locations?: string[]; name?: string; firm?: string },
): Promise<{ data: { inviteId: string; expiresAt: string } }> {
  return mutate(`/api/tenants/${tenantId}/tenant-users`, 'POST', body) as Promise<{
    data: { inviteId: string; expiresAt: string };
  }>;
}
export async function setTenantUserStatus(
  tenantId: string,
  userId: string,
  status: 'active' | 'suspended',
  reason: string,
): Promise<void> {
  await mutate(`/api/tenants/${tenantId}/tenant-users/${userId}/status`, 'POST', {
    status,
    reason,
  });
}
export async function grantTenantUserRole(
  tenantId: string,
  userId: string,
  roleId: string,
  locationId?: string | null,
): Promise<void> {
  await mutate(`/api/tenants/${tenantId}/tenant-users/${userId}/role`, 'PUT', {
    roleId,
    ...(locationId !== undefined ? { locationId } : {}),
  });
}

// ── Tenant roles (Task 13 step 3) ────────────────────────────────────────────
export interface TenantRoleRow {
  id: string;
  name: string;
  isBuiltIn: boolean;
  isAssignable: boolean;
  isElevated: boolean;
  permissionCount: number;
  userCount: number;
  archivedAt: string | null;
  groupKeys: string[];
}
export interface TenantRoleDetail {
  id: string;
  name: string;
  description: string | null;
  permissions: string[];
  rowVersion: number;
}
export async function fetchTenantRoles(
  tenantId: string,
  includeArchived: boolean,
): Promise<{ roles: TenantRoleRow[] }> {
  const q = includeArchived ? '?includeArchived=true' : '';
  return json(await authenticatedFetch(`/api/tenants/${tenantId}/tenant-roles${q}`));
}
export async function fetchTenantRole(
  tenantId: string,
  roleId: string,
): Promise<{ role: TenantRoleDetail }> {
  return json(await authenticatedFetch(`/api/tenants/${tenantId}/tenant-roles/${roleId}`));
}
export async function createTenantRole(
  tenantId: string,
  body: { displayName: string; description?: string; permissions: string[] },
): Promise<unknown> {
  return mutate(`/api/tenants/${tenantId}/tenant-roles`, 'POST', body);
}
export async function updateTenantRole(
  tenantId: string,
  roleId: string,
  body: {
    displayName?: string;
    description?: string;
    permissions?: string[];
    expectedVersion: number;
  },
): Promise<unknown> {
  return mutate(`/api/tenants/${tenantId}/tenant-roles/${roleId}`, 'PATCH', body);
}
export async function archiveTenantRole(
  tenantId: string,
  roleId: string,
  expectedVersion: number,
  reason?: string,
): Promise<unknown> {
  return mutate(`/api/tenants/${tenantId}/tenant-roles/${roleId}/archive`, 'POST', {
    expectedVersion,
    ...(reason ? { reason } : {}),
  });
}

// The TENANT permission vocabulary (divergence D6 -- a separate namespace from
// the admin bitmap catalogue PermissionBitPicker renders). Server-computed
// from @heyroot/contracts' PERMISSION_METADATA sidecar so the ~484-entry
// catalogue never bundles into the browser (same reason MODULE_KEYS is
// served, not shipped).
export interface TenantPermissionMeta {
  name: string;
  module: string | null;
  description: string | null;
}
export async function fetchTenantPermissions(): Promise<{ permissions: TenantPermissionMeta[] }> {
  return json(await authenticatedFetch('/api/meta/tenant-permissions'));
}

// ── Pending owner invites (Task 13 step 5b -- ownerless tenant state) ──────
// Closes a spec §5 gap: §6.1's route table never provisioned a read for this.
// Never carries token_hash -- the repo/route deliberately never select it.
export interface PendingInviteRow {
  id: string;
  targetEmail: string;
  targetRoleId: string;
  inviterId: string;
  expiresAt: string;
  createdAt: string;
}
export async function fetchPendingInvites(tenantId: string): Promise<{ rows: PendingInviteRow[] }> {
  return json(await authenticatedFetch(`/api/tenants/${tenantId}/pending-invites`));
}

// Re-send the owner invite of an OWNERLESS tenant (#14813). No input on purpose: the server
// picks the address (the tenant's own pending invite / primary email), so a browser can never
// aim an owner invite at an arbitrary address. The response never carries the token.
export interface ReissueOwnerInviteResult {
  data: { tenantId: string; targetEmail: string };
  inviteEmail: { ok: true; outcome: string } | { ok: false; reason: string };
  auditFailed?: boolean;
}
export async function reissueOwnerInvite(tenantId: string): Promise<ReissueOwnerInviteResult> {
  return mutate(
    `/api/tenants/${tenantId}/owner-invite`,
    'POST',
    {},
  ) as Promise<ReissueOwnerInviteResult>;
}

// ── Audit log (Task 13 step 4) ───────────────────────────────────────────────
export interface TenantAuditRow {
  id: string;
  actorUid: string;
  action: string;
  target: unknown;
  detail: unknown;
  at: string;
}
export async function fetchTenantAudit(
  tenantId: string,
  opts: { cursor?: string; limit?: number } = {},
): Promise<{ rows: TenantAuditRow[]; nextCursor: string | null }> {
  const q = new URLSearchParams();
  if (opts.cursor) q.set('cursor', opts.cursor);
  if (opts.limit != null) q.set('limit', String(opts.limit));
  const suffix = q.toString() ? `?${q.toString()}` : '';
  return json(await authenticatedFetch(`/api/tenants/${tenantId}/audit${suffix}`));
}

// ── Tenant creation (Task 12) ────────────────────────────────────────────────
export interface CreateTenantBody {
  displayName: string;
  slug?: string;
  ownerEmail: string;
  businessTypeSlug?: string;
  timezone?: string;
}
export interface CreateTenantResult {
  data: { tenantId: string; slug: string; status: string };
  inviteEmail: unknown;
}
export async function createTenant(body: CreateTenantBody): Promise<CreateTenantResult> {
  return mutate('/api/tenants', 'POST', body) as Promise<CreateTenantResult>;
}

// ── Cross-tenant module matrix (Task 14) ─────────────────────────────────────
export interface ModuleMatrixParams {
  status?: string;
  /** A `tenants.tenants.business_type` SLUG (fix round 1) -- e.g. 'mixed', not a display label. */
  businessType?: string;
  limit?: number;
  offset?: number;
}
export interface ModuleMatrixTenantRow {
  tenantId: string;
  displayName: string;
  status: string;
  cells: Record<string, 'off' | 'view' | 'full' | 'locked'>;
}
export async function fetchModuleMatrix(
  p: ModuleMatrixParams,
): Promise<{ rows: ModuleMatrixTenantRow[]; total: number }> {
  const q = new URLSearchParams();
  if (p.status) q.set('status', p.status);
  if (p.businessType) q.set('businessType', p.businessType);
  if (p.limit != null) q.set('limit', String(p.limit));
  if (p.offset != null) q.set('offset', String(p.offset));
  return json(await authenticatedFetch(`/api/modules/matrix?${q.toString()}`));
}

// Filter-option source (fix round 1) -- distinct business-type SLUGS actually
// present on tenants.tenants, including the 'mixed' column default (never a
// row in the business-type registry, so fetchBusinessTypes() would omit it).
export async function fetchMatrixBusinessTypes(): Promise<{ businessTypes: string[] }> {
  return json(await authenticatedFetch('/api/modules/matrix/business-types'));
}

export async function fetchAnalytics(p: AnalyticsParams): Promise<{ rows: MetricsDailyRow[] }> {
  const q = new URLSearchParams({ from: p.from, to: p.to });
  if (p.tenantId) q.set('tenantId', p.tenantId);
  return json<{ rows: MetricsDailyRow[] }>(
    await authenticatedFetch(`/api/assistant/analytics?${q.toString()}`),
  );
}

// ─── Cost-plus messaging billing ─────────────────────────────────────────

export async function fetchSuppliers(): Promise<SupplierRow[]> {
  const res = await json<{ suppliers: SupplierRow[] }>(
    await authenticatedFetch('/api/cost-plus/suppliers'),
  );
  return res.suppliers;
}

export async function upsertSupplier(body: UpsertSupplierBody): Promise<void> {
  await json(
    await authenticatedFetch(`/api/cost-plus/suppliers/${encodeURIComponent(body.supplierKey)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
}

export async function fetchSupplyRates(supplierKey: string): Promise<SupplyRateRow[]> {
  const res = await json<{ rates: SupplyRateRow[] }>(
    await authenticatedFetch(`/api/cost-plus/suppliers/${encodeURIComponent(supplierKey)}/rates`),
  );
  return res.rates;
}

export async function addSupplyRate(
  supplierKey: string,
  body: AddRateBody,
): Promise<{ id: string }> {
  return json(
    await authenticatedFetch(`/api/cost-plus/suppliers/${encodeURIComponent(supplierKey)}/rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
}

export async function fetchTenantPlans(): Promise<TenantPlanRow[]> {
  const res = await json<{ plans: TenantPlanRow[] }>(
    await authenticatedFetch('/api/cost-plus/plans'),
  );
  return res.plans;
}

export async function upsertTenantPlan(tenantId: string, body: UpsertPlanBody): Promise<void> {
  await json(
    await authenticatedFetch(`/api/cost-plus/plans/${encodeURIComponent(tenantId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }),
  );
}

export async function fetchPlatformMargin(period: string): Promise<PlatformMargin> {
  return json(
    await authenticatedFetch(`/api/cost-plus/margin?period=${encodeURIComponent(period)}`),
  );
}
